<template>
  <div class="container py-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>Designs</h2>
      <BackButton fallback="teams-settings" :params="{ id: $route.params.id }" />
    </div>

    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mb-4">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
            Bereiche
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link :to="{ name: 'teams-detail', params: { id: $route.params.id } }" class="text-decoration-none">
            {{ selectedTeam?.name }}
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link :to="{ name: 'teams-settings', params: { id: $route.params.id } }" class="text-decoration-none">
            Bereich-Einstellungen
          </router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">Designs</li>
      </ol>
    </nav>

    <!-- Loading State -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Laden...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <!-- Content -->
    <div v-else-if="selectedTeam">
      <!-- Activated Templates Section -->
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0 d-flex align-items-center">
                <font-awesome-icon icon="check-circle" class="me-2 text-success" />
                Aktivierte Designs
                <span class="badge bg-success ms-2">{{ teamTemplates.length }}</span>
              </h5>
              <div class="mt-2">
                <p class="mb-1 text-muted">
                  <font-awesome-icon icon="info-circle" class="me-1" />
                  Designs definieren die verfügbaren Felder für deine Quittungen. Jedes Design kann individuell angepasst werden.
                </p>
                <div class="row g-2 mt-2">
                  <div class="col-md-4">
                    <small class="text-muted">
                      <font-awesome-icon icon="list" class="me-1 text-primary" />
                      <strong>Felder:</strong> Verwalte die verfügbaren Eingabefelder
                    </small>
                  </div>
                  <div class="col-md-4">
                    <small class="text-muted">
                      <font-awesome-icon icon="file-alt" class="me-1 text-info" />
                      <strong>Vorlagen:</strong> Erstelle ausgefüllte Vorlagen
                    </small>
                  </div>
                  <div class="col-md-4">
                    <small class="text-muted">
                      <font-awesome-icon icon="edit" class="me-1 text-secondary" />
                      <strong>Bearbeiten:</strong> Namen ändern und als Standard setzen
                    </small>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <!-- Existing Templates -->
                <div 
                  v-for="teamTemplate in teamTemplates" 
                  :key="teamTemplate.template.id"
                  class="col-md-6"
                >
                  <div class="card activated-template-card h-100 border-0 shadow-sm ">
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-start mb-3">
                        <div class="flex-grow-1">
                          <div class="d-flex align-items-center mb-2">
                            <h6 class="mb-0 me-2">{{ getTemplateDisplayName(teamTemplate) }}</h6>
                            <span 
                              v-if="teamTemplate.isDefault" 
                              class="badge bg-warning text-dark"
                              title="Standard-Design - wird automatisch für neue Quittungen verwendet"
                            >
                              <font-awesome-icon icon="star" class="me-1" />
                              Standard
                            </span>
                            <span 
                              v-if="teamTemplate.template.isProFeature" 
                              class="badge bg-primary ms-1"
                              title="Pro-Feature - nur für Bereiche mit Pro-Zugang"
                            >
                              <font-awesome-icon icon="crown" class="me-1" />
                              Pro
                            </span>
                            <span 
                              v-if="teamTemplate.needsSignature === false" 
                              class="badge bg-secondary ms-1"
                              title="Keine Signatur erforderlich"
                            >
                              <font-awesome-icon icon="signature" class="me-1" />
                              Keine Signatur
                            </span>
                          </div>
                          <small class="text-muted d-block mb-2">
                            <font-awesome-icon icon="tag" class="me-1" />
                            {{ teamTemplate.template.name }}
                          </small>
                          <p v-if="teamTemplate.template.description" class="small text-muted mb-3">
                            {{ teamTemplate.template.description }}
                          </p>
                        </div>
                      </div>
                      
                      <!-- Template Info -->
                      <div class="mb-3">
                        <div class="row g-2 text-center">
                          <div class="col-6">
                            <div class="p-2 bg-light rounded">
                              <div class="fw-bold text-primary">{{ teamTemplate.template?.fields?.length || 0 }}</div>
                              <small class="text-muted">Felder</small>
                            </div>
                          </div>
                          <div class="col-6">
                            <div class="p-2 bg-light rounded">
                              <div class="fw-bold text-info">{{ teamTemplate.template.category?.displayName || 'Allgemein' }}</div>
                              <small class="text-muted">Kategorie</small>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Action Buttons -->
                      <div class="d-grid gap-2">
                        <div class="btn-group" role="group">
                          <button 
                            type="button"
                            class="btn btn-primary btn-sm"
                            @click.stop="goToFieldsForTemplate(teamTemplate)"
                            title="Felder für dieses Design verwalten"
                          >
                            <font-awesome-icon icon="list" class="me-1" />
                            Felder
                          </button>
                          <button 
                            type="button"
                            class="btn btn-info btn-sm text-white"
                            @click.stop="goToDraftsForTemplate(teamTemplate)"
                            title="Ausgefüllte Vorlagen für dieses Design verwalten"
                          >
                            <font-awesome-icon icon="file-alt" class="me-1" />
                            Vorlagen
                          </button>
                          <button
                            type="button"
                            class="btn btn-outline-primary btn-sm"
                            @click.stop="openTitleSettingsModal(teamTemplate)"
                            title="Titel für dieses Design konfigurieren"
                          >
                            <font-awesome-icon icon="font" class="me-1" />
                            Titel
                          </button>
                          <button 
                            type="button"
                            class="btn btn-primary btn-sm text-white"
                            @click.stop="openEditNameModal(teamTemplate)"
                            title="Design-Namen bearbeiten und als Standard setzen"
                          >
                            <font-awesome-icon icon="edit" class="me-1" />
                            Bearbeiten
                          </button>
                        </div>
                        <button 
                          type="button"
                          class="btn btn-danger btn-sm"
                          @click.stop="removeActivatedTemplate(teamTemplate)"
                          :disabled="!canDeactivateTemplate(teamTemplate)"
                          :title="!canDeactivateTemplate(teamTemplate) ? 
                            'Standard-Design kann nicht gelöscht werden. Mindestens ein Design muss aktiv bleiben.' : 
                            'Design löschen (alle zugehörigen Felder werden ebenfalls gelöscht)'"
                        >
                          <font-awesome-icon icon="trash" class="me-1" />
                          {{ !canDeactivateTemplate(teamTemplate) ? 'Geschützt' : 'Entfernen' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Add New Design Card (always last) -->
                <div class="col-md-6">
                  <div class="card add-template-card h-100 border-2 border-dashed border-primary" @click="openAssignTemplateModal">
                    <div class="card-body d-flex flex-column align-items-center justify-content-center text-center py-5">
                      <div class="mb-3">
                        <font-awesome-icon icon="plus-circle" size="3x" class="text-primary" />
                      </div>
                      <h6 class="card-title text-primary mb-1">Neues Design hinzufügen</h6>
                      <p class="card-text text-muted small mb-0">
                        Weise ein neues Design zu
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        

      </div>
    </div>
    
    <!-- Team not found -->
    <div v-else class="alert alert-gradient-warning" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      Team nicht gefunden.
    </div>
  </div>

  <!-- Edit Template Name Modal -->
  <div 
    v-if="showEditNameModal"
    class="modal show" 
    id="editNameModal" 
    tabindex="-1" 
    aria-labelledby="editNameModalLabel" 
    aria-hidden="false"
    style="display: block;"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="editNameModalLabel">
            <font-awesome-icon icon="edit" class="me-2" />
            Design-Namen bearbeiten
          </h5>
          <button 
            type="button" 
            class="btn-close" 
            @click="showEditNameModal = false"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label for="customName" class="form-label">Benutzerdefinierter Name</label>
            <input 
              id="customName"
              v-model="editNameForm.customName"
              type="text" 
              class="form-control"
              :placeholder="editNameForm.originalName"
              maxlength="255"
            >
            <div class="form-text">
              Lass das Feld leer, um den ursprünglichen Namen zu verwenden: "{{ editNameForm.originalName }}"
            </div>
          </div>
          
          <!-- Set as Default Button -->
          <div class="mb-3">
            <button 
              type="button"
              class="btn btn-outline-primary"
              @click="setAsDefault"
              :disabled="settingAsDefault || editNameForm.isDefault"
              :title="editNameForm.isDefault ? 'Dieses Template ist bereits das Standard-Template' : 'Dieses Template als Standard für den Bereich setzen'"
            >
              <font-awesome-icon icon="star" class="me-2" />
              <span v-if="settingAsDefault" class="spinner-border spinner-border-sm me-2"></span>
              {{ editNameForm.isDefault ? 'Bereits Standard' : (settingAsDefault ? 'Setzen...' : 'Als Standard setzen') }}
            </button>
            <div class="form-text">
              <font-awesome-icon icon="info-circle" class="me-1" />
              Standard-Designs werden automatisch beim Erstellen neuer Quittungen verwendet.
            </div>
          </div>

          <!-- Signature Requirement Toggle -->
          <div class="ms-4">
            <div class="form-check form-switch ps-3">
              <input 
                class="form-check-input" 
                type="checkbox" 
                id="needsSignatureToggle"
                v-model="editNameForm.needsSignature"
                :disabled="updatingSignature"
              >
              <label class="form-check-label ms-2" for="needsSignatureToggle">
                <font-awesome-icon icon="signature" class="me-1" />
                Signatur erforderlich
              </label>
            </div>
          </div>
          <div class="form-text mb-3">
              <font-awesome-icon icon="info-circle" class="me-1" />
              Bestimmt ob Quittungen mit diesem Design eine Signatur benötigen.
            </div>
          <!-- Quick Actions -->
          <div class="mb-3">
            <label class="form-label">Schnellzugriff</label>
            <div class="row g-2">
              <div class="col-6">
                <div class="card h-100 border-0 shadow-sm hover-card" @click="goToFieldsFromEditModal" style="cursor: pointer;">
                  <div class="card-body text-center py-3">
                    <font-awesome-icon icon="list" size="lg" class="text-primary mb-2" />
                    <h6 class="card-title mb-1">Felder verwalten</h6>
                    <p class="card-text small text-muted mb-0">Template-Felder bearbeiten</p>
                  </div>
                </div>
              </div>
              <div class="col-6">
                <div class="card h-100 border-0 shadow-sm hover-card" @click="goToDraftsFromEditModal" style="cursor: pointer;">
                  <div class="card-body text-center py-3">
                    <font-awesome-icon icon="file-alt" size="lg" class="text-info mb-2" />
                    <h6 class="card-title mb-1">Vorlagen verwalten</h6>
                    <p class="card-text small text-muted mb-0">Ausgefüllte Vorlagen verwalten</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="form-text mt-2">
              <font-awesome-icon icon="info-circle" class="me-1" />
              Direkter Zugriff auf Felder und ausgefüllte Vorlagen für dieses Design.
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button 
            type="button" 
            class="btn btn-secondary" 
            @click="showEditNameModal = false"
          >
            Abbrechen
          </button>
          <button 
            type="button" 
            class="btn btn-primary"
            @click="saveCustomName"
            :disabled="updatingName"
          >
            <span v-if="updatingName" class="spinner-border spinner-border-sm me-2"></span>
            {{ updatingName ? 'Speichern...' : 'Speichern' }}
          </button>
        </div>
      </div>
  </div>
</div>

  <!-- Title Settings Modal -->
  <div 
    v-if="showTitleSettingsModal"
    class="modal show"
    tabindex="-1"
    aria-labelledby="titleSettingsModalLabel"
    aria-hidden="false"
    style="display: block;"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="titleSettingsModalLabel">
            <font-awesome-icon icon="font" class="me-2" />
            Titel konfigurieren
          </h5>
          <button 
            type="button" 
            class="btn-close" 
            @click="closeTitleSettingsModal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedTemplateForTitle">
            <p class="text-muted small mb-3">
              Die Titelvorlage steuert die Anzeige in Listen und Detailseiten.
            </p>
            <div class="mb-3">
              <label for="titlePatternInput" class="form-label">Titelvorlage (Listenansicht)</label>
              <textarea 
                id="titlePatternInput"
                ref="titlePatternInput"
                v-model="titleSettingsForm.pattern"
                rows="2"
                class="form-control"
                :placeholder="'z. B. {{sr_vorname}} - {{sr_nachname}}'"
              ></textarea>
              <div class="form-text">
                Nutze Platzhalter wie <code v-text="'{{feldname}}'"></code>, um Daten aus den Feldern im Titel zu verwenden.
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">Verfügbare Platzhalter</label>
              <div class="d-flex flex-wrap gap-2">
                <button 
                  v-for="option in titlePlaceholderOptions"
                  :key="option.key"
                  type="button"
                  class="btn btn-outline-secondary btn-sm"
                  @click="insertPlaceholder(option.key)"
                >
                  <font-awesome-icon icon="plus" class="me-1" />
                  {{ option.label }}
                </button>
              </div>
              <div class="form-text">
                Klick auf einen Platzhalter, um ihn an der aktuellen Cursor-Position einzufügen.
              </div>
            </div>
            <div class="mb-0">
              <label class="form-label">Vorschau</label>
              <div class="alert alert-secondary mb-0">
                {{ titleSettingsForm.preview || 'Der gespeicherte Standardtitel wird verwendet.' }}
              </div>
            </div>
            <hr class="my-4">
            <h6 class="mb-2">PDF-Titel (Aufdruck)</h6>
            <p class="text-muted small mb-3">
              Dieser Text erscheint als sichtbarer Titel auf dem generierten PDF. Leer lassen, um den Template-Standard zu übernehmen.
            </p>
            <div class="mb-3">
              <label for="pdfTitleInput" class="form-label">PDF-Titel</label>
              <input
                id="pdfTitleInput"
                v-model="titleSettingsForm.pdfTitle"
                type="text"
                class="form-control"
                :placeholder="selectedTemplateForTitle?.template?.pdfTitle || 'Quittung'"
                maxlength="255"
              >
            </div>
          </div>
          <div v-else class="text-muted">
            Kein Design ausgewählt.
          </div>
        </div>
        <div class="modal-footer">
          <button 
            type="button" 
            class="btn btn-outline-secondary"
            @click="titleSettingsForm.pattern = ''"
            :disabled="savingTitleSettings"
          >
            Zurücksetzen
          </button>
          <button 
            type="button" 
            class="btn btn-secondary" 
            @click="closeTitleSettingsModal"
            :disabled="savingTitleSettings"
          >
            Abbrechen
          </button>
          <button 
            type="button" 
            class="btn btn-primary"
            @click="saveTitleSettings"
            :disabled="savingTitleSettings"
          >
            <span v-if="savingTitleSettings" class="spinner-border spinner-border-sm me-2"></span>
            {{ savingTitleSettings ? 'Speichern...' : 'Speichern' }}
          </button>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Fields Management Modal -->
  <div 
    v-if="showFieldsModal"
    class="modal show" 
    id="fieldsModal" 
    tabindex="-1" 
    aria-labelledby="fieldsModalLabel" 
    aria-hidden="false"
    style="display: block;"
  >
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="fieldsModalLabel">
            <font-awesome-icon icon="list" class="me-2" />
            Felder verwalten
          </h5>
          <button 
            type="button" 
            class="btn-close" 
            @click="showFieldsModal = false"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <!-- Design Selection -->
          <div class="mb-4">
            <label for="templateSelect" class="form-label">Design auswählen:</label>
            <select 
              id="templateSelect"
              v-model="selectedTemplateForFields" 
              class="form-select"
            >
              <option value="">Design auswählen...</option>
              <option 
                v-for="teamTemplate in teamTemplates" 
                :key="teamTemplate.id"
                :value="teamTemplate"
              >
                {{ getTemplateDisplayName(teamTemplate) }}
              </option>
            </select>
          </div>

          <!-- Fields List -->
          <div v-if="selectedTemplateForFields">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0">Design-Felder ({{ templateFields.length }})</h6>
              <div class="d-flex gap-2">
                <div class="btn-group btn-group-sm">
                  <button 
                    @click="toggleOrderMode(activeTab)"
                    class="btn btn-outline-secondary"
                    :class="{ 'active': orderMode === activeTab }"
                    title="Reihenfolge ändern"
                  >
                    <font-awesome-icon icon="arrows-alt" class="me-1" />
                    {{ orderMode === activeTab ? 'Beenden' : 'Reihenfolge' }}
                  </button>
                  <button 
                    v-if="orderMode === activeTab"
                    @click="saveFieldOrder(activeTab)"
                    class="btn btn-success"
                    :disabled="savingOrder"
                  >
                    <font-awesome-icon icon="save" class="me-1" />
                    {{ savingOrder ? 'Speichern...' : 'Speichern' }}
                  </button>
                </div>
                <button 
                  @click="openCreateFieldModal"
                  class="btn btn-primary btn-sm"

                  v-if="selectedTemplateForFields.template.allowCustomFields"
                  :title="!selectedTemplateForFields.template.allowCustomFields ? 'Benutzerdefinierte Felder sind für dieses Design nicht erlaubt' : 'Neues Feld erstellen'"
                >
                  <font-awesome-icon icon="plus" class="me-1" />
                  Neues Feld
                </button>
              </div>
            </div>
            <div v-if="!selectedTemplateForFields.template.allowCustomFields" class="alert  alert-gradient-warning">
              Benutzerdefinierte Felder sind für dieses Design nicht erlaubt
            </div>
            <!-- Tabs for Categories -->
            <ul class="nav nav-tabs mb-3" role="tablist">
              <li class="nav-item" role="presentation">
                <button 
                  class="nav-link" 
                  :class="{ active: activeTab === 'receipt' }"
                  @click="activeTab = 'receipt'"
                  type="button"
                  role="tab"
                >
                  <font-awesome-icon icon="receipt" class="me-1" />
                  Quittung ({{ templateFields.filter(field => field.category === 'receipt').length }})
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button 
                  class="nav-link" 
                  :class="{ active: activeTab === 'financial' }"
                  @click="activeTab = 'financial'"
                  type="button"
                  role="tab"
                >
                  <font-awesome-icon icon="euro-sign" class="me-1" />
                  Finanziell ({{ templateFields.filter(field => field.category === 'financial').length }})
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button 
                  class="nav-link" 
                  :class="{ active: activeTab === 'personal' }"
                  @click="activeTab = 'personal'"
                  type="button"
                  role="tab"
                >
                  <font-awesome-icon icon="user" class="me-1" />
                  Persönlich ({{ templateFields.filter(field => field.category === 'personal').length }})
                </button>
              </li>
            </ul>

            <!-- Field Groups Management -->
            <div class="card mb-3">
              <div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                  <h6 class="mb-0">Gruppen für {{ getFieldCategoryLabel(activeTab) }}</h6>
                  <small class="text-muted">Gruppiere Felder zu farbigen Bereichen im Quittungsassistenten.</small>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    @click="addFieldGroup(activeTab)"
                    :disabled="!activeTab"
                  >
                    <font-awesome-icon icon="layer-group" class="me-1" />
                    Gruppe hinzufügen
                  </button>
                </div>
              </div>
              <div class="card-body">
                <div v-if="getFieldGroups(activeTab).length === 0" class="text-muted small">
                  Noch keine Gruppen vorhanden. Füge eine Gruppe hinzu, um Felder visuell zu bündeln.
                </div>
                <div v-else class="field-group-list">
                  <div
                    v-for="(group, index) in getFieldGroups(activeTab)"
                    :key="group.id"
                    class="field-group-item border rounded p-3 mb-2"
                  >
                    <div class="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3">
                      <div class="d-flex align-items-center gap-2 flex-grow-1 w-100">
                        <span
                          class="group-color-dot"
                          :style="{ backgroundColor: group.color }"
                        ></span>
                        <input
                          type="text"
                          class="form-control"
                          v-model="group.name"
                          placeholder="Gruppenname"
                          @input="markFieldGroupsDirty()"
                        />
                      </div>
                      <div class="d-flex align-items-center gap-3 flex-wrap">
                        <div class="d-flex align-items-center gap-2">
                          <label class="form-label small mb-0 text-muted">Farbe</label>
                          <input
                            type="color"
                            class="form-control form-control-color"
                            v-model="group.color"
                            @input="markFieldGroupsDirty()"
                            aria-label="Gruppenfarbe wählen"
                          />
                        </div>
                        <span class="badge bg-light text-dark">
                          {{ countFieldsInGroup(activeTab, group.id) }} Feld(er)
                        </span>
                        <div class="btn-group btn-group-sm" role="group" aria-label="Gruppenreihenfolge">
                          <button
                            type="button"
                            class="btn btn-outline-secondary"
                            @click="moveFieldGroup(activeTab, index, -1)"
                            :disabled="index === 0"
                            title="Nach oben"
                          >
                            <font-awesome-icon icon="arrow-up" />
                          </button>
                          <button
                            type="button"
                            class="btn btn-outline-secondary"
                            @click="moveFieldGroup(activeTab, index, 1)"
                            :disabled="index === getFieldGroups(activeTab).length - 1"
                            title="Nach unten"
                          >
                            <font-awesome-icon icon="arrow-down" />
                          </button>
                        </div>
                        <button
                          type="button"
                          class="btn btn-outline-danger btn-sm"
                          @click="removeFieldGroup(activeTab, group)"
                          title="Gruppe löschen"
                        >
                          <font-awesome-icon icon="trash" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-footer d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                <small class="text-muted">
                  Änderungen an Gruppen müssen gespeichert werden, damit sie im Wizard erscheinen.
                </small>
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  @click="saveFieldGroups"
                  :disabled="!fieldGroupsDirty || savingFieldGroups"
                >
                  <span v-if="savingFieldGroups" class="spinner-border spinner-border-sm me-2"></span>
                  {{ savingFieldGroups ? 'Speichern...' : 'Gruppen speichern' }}
                </button>
              </div>
            </div>

            <!-- Fields Table -->
            <div class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th v-if="orderMode === activeTab" style="width: 50px;">#</th>
                    <th>Name</th>
                    <th>Typ</th>
                    <th>Erforderlich</th>
                    <th>Beschreibung</th>
                    <th>Gruppe</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="field in templateFields.filter(field => field.category === activeTab)" 
                    :key="field.id"
                    :class="{ 'table-warning': orderMode === activeTab }"
                    draggable="true"
                    @dragstart="dragStart($event, field, activeTab)"
                    @dragover.prevent
                    @drop="drop($event, field, activeTab)"
                  >
                    <td v-if="orderMode === activeTab" class="text-center">
                      <font-awesome-icon icon="grip-vertical" class="text-muted" />
                    </td>
                    <td>
                      <strong>{{ field.displayName }}</strong>
                      <br>
                      <div class="mt-1">
                        <span v-if="field.isCalculated" class="badge bg-info me-2">
                          <font-awesome-icon icon="calculator" class="me-1" />
                          Berechnet
                        </span>
                        <small class="text-muted text-small text-italic text-warning" v-if="field.undeletable">Feld nicht löschbar</small>
                      </div>
                    </td>
                    <td>
                      <span class="badge bg-secondary">{{ getFieldTypeLabel(field.type) }}</span>
                    </td>
                    <td>
                      <span v-if="field.required" class="badge bg-danger">Erforderlich</span>
                      <span v-else class="text-muted">Optional</span>
                    </td>
                    <td>
                      <small class="text-muted">{{ field.description || '-' }}</small>
                    </td>
                    <td>
                      <div class="d-flex align-items-center gap-2 flex-wrap">
                        <span
                          class="group-color-dot"
                          v-if="field.options?.groupId"
                          :style="{ backgroundColor: getGroupColor(activeTab, field.options.groupId) }"
                          :title="getGroupName(activeTab, field.options.groupId)"
                        ></span>
                        <select
                          class="form-select form-select-sm"
                          :value="field.options?.groupId || ''"
                          :disabled="orderMode === activeTab || updatingFieldGroupId === field.id"
                          @change="onFieldGroupAssignmentChange(field, activeTab, $event.target.value)"
                        >
                          <option value="">Keine Gruppe</option>
                          <option
                            v-for="group in getFieldGroups(activeTab)"
                            :key="group.id"
                            :value="group.id"
                          >
                            {{ group.name }}
                          </option>
                        </select>
                        <span
                          v-if="updatingFieldGroupId === field.id"
                          class="spinner-border spinner-border-sm text-muted"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      </div>
                    </td>
                    <td>
                      <div class="btn-group btn-group-sm">
                        <button 
                          @click="editField(field)"
                          class="btn btn-outline-primary"
                          title="Feld bearbeiten"
                          :disabled="orderMode === activeTab"
                        >
                          <font-awesome-icon icon="edit" />
                        </button>
                        <button 
                          @click="deleteField(field)"
                          class="btn btn-outline-danger"
                          :disabled="field.undeletable || orderMode === activeTab"
                          :title="field.undeletable ? 'Fixed-Felder können nicht gelöscht werden' : 'Feld löschen'"
                        >
                          <font-awesome-icon icon="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="templateFields.filter(field => field.category === activeTab).length === 0" class="text-center py-4 text-muted">
              <font-awesome-icon icon="list" size="2x" class="mb-3" />
              <p>Keine Felder in dieser Kategorie</p>
              <small>Verwende den "Neues Feld" Button, um Felder hinzuzufügen.</small>
            </div>
          </div>

          <div v-else class="text-center py-4 text-muted">
            <font-awesome-icon icon="list" size="2x" class="mb-3" />
            <p>Bitte wähle ein Design aus</p>
            <small>Wähle ein Design aus der Dropdown-Liste oben aus.</small>
          </div>
        </div>
        <div class="modal-footer">
          <button 
            type="button" 
            class="btn btn-secondary" 
            @click="showFieldsModal = false"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Create Field Modal -->
  <div 
    v-if="showCreateFieldModal"
    class="modal show"
    tabindex="-1"
    aria-labelledby="createFieldModalLabel"
    aria-hidden="false"
    style="display: block;"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="createFieldModalLabel">
            <font-awesome-icon icon="plus" class="me-2" />
            Neues Feld erstellen
          </h5>
          <button 
            type="button" 
            class="btn-close" 
            @click="showCreateFieldModal = false"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="createField">
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="fieldName" class="form-label">Interner Name *</label>
                  <input 
                    id="fieldName"
                    v-model="newField.name"
                    type="text" 
                    class="form-control"
                    placeholder="z.B. customer_name"
                    required
                  >
                  <div class="form-text">Eindeutiger Name für das Feld (nur Buchstaben, Zahlen, Unterstriche)</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="fieldDisplayName" class="form-label">Anzeigename *</label>
                  <input 
                    id="fieldDisplayName"
                    v-model="newField.displayName"
                    type="text" 
                    class="form-control"
                    placeholder="z.B. Kundename"
                    required
                  >
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="fieldType" class="form-label">Feldtyp *</label>
                  <select 
                    id="fieldType"
                    v-model="newField.type"
                    class="form-select"
                    required
                  >
                    <option v-if="newField.category !== 'financial'" value="text">Text</option>
                    <option v-if="newField.category !== 'financial'" value="number">Zahl</option>
                    <option v-if="newField.category === 'receipt'" value="location">Ort</option>
                    <option v-if="newField.category !== 'financial'" value="date">Datum</option>
                    <option v-if="newField.category !== 'financial'" value="textarea">Textbereich</option>
                    <option v-if="newField.category !== 'financial'" value="file">Datei</option>
                    <option v-if="newField.category !== 'financial'" value="select">Auswahl</option>
                    <option v-if="newField.category === 'financial'" value="money">Geldbetrag</option>
                    <option v-if="newField.category === 'receipt'" value="repeater">Repeater (Wiederholbare Positionen)</option>
                  </select>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="fieldCategory" class="form-label">Kategorie *</label>
                  <select 
                    id="fieldCategory"
                    v-model="newField.category"
                    class="form-select"
                    required
                    @change="e => changeTab(e)"
                  >
                    <option value="receipt">Quittung</option>
                    <option value="financial">Finanziell</option>
                    <option value="personal">Persönlich</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="row" v-if="getFieldGroups(newField.category).length > 0">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="fieldGroup" class="form-label">Gruppe</label>
                  <select
                    id="fieldGroup"
                    v-model="newField.options.groupId"
                    class="form-select"
                  >
                    <option :value="null">Keine Gruppe</option>
                    <option
                      v-for="group in getFieldGroups(newField.category)"
                      :key="group.id"
                      :value="group.id"
                    >
                      {{ group.name }}
                    </option>
                  </select>
                  <div class="form-text">
                    Ordne dieses Feld einer Gruppe zu, um es im Wizard visuell hervorzuheben.
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="fieldPlaceholder" class="form-label">Placeholder</label>
                  <input 
                    id="fieldPlaceholder"
                    v-model="newField.placeholder"
                    type="text" 
                    class="form-control"
                    placeholder="z.B. Kundename eingeben"
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="fieldDefaultValue" class="form-label">Standardwert</label>
                  <input 
                    id="fieldDefaultValue"
                    v-model="newField.defaultValue"
                    type="text" 
                    class="form-control"
                    placeholder="Standardwert für das Feld"
                  >
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label for="fieldDescription" class="form-label">Beschreibung</label>
              <textarea 
                id="fieldDescription"
                v-model="newField.description"
                class="form-control"
                rows="3"
                placeholder="Beschreibung des Feldes"
              ></textarea>
            </div>

            <div class="mb-3">
              <div class="form-check">
                <input 
                  id="fieldRequired"
                  v-model="newField.required"
                  type="checkbox" 
                  class="form-check-input"
                >
                <label class="form-check-label" for="fieldRequired">
                  Erforderlich
                </label>
              </div>
            </div>

            <div class="mb-3">
              <div class="form-check">
                <input 
                  id="fieldAllowPrefill"
                  v-model="newField.allowPrefill"
                  type="checkbox" 
                  class="form-check-input"
                >
                <label class="form-check-label" for="fieldAllowPrefill">
                  Im Profil vorausfüllbar
                </label>
                <div class="form-text">
                  Ermöglicht Mitgliedern, dieses Feld im Profil vorzubereiten.
                </div>
              </div>
            </div>
            
            <!-- Calculated Field Options (only for money type) -->
            <div v-if="newField.type === 'money'" class="mb-3">
              <div class="form-check">
                <input 
                  v-model="newField.isCalculated"
                  class="form-check-input"
                  type="checkbox"
                  id="fieldIsCalculated"
                >
                <label class="form-check-label" for="fieldIsCalculated">
                  Berechnetes Feld (Preis pro Einheit)
                </label>
                <div class="form-text">
                  Das Feld wird automatisch basierend auf einer Menge und einem Preis pro Einheit berechnet
                </div>
              </div>
            </div>
            
            <!-- Per Unit Calculation Configuration -->
            <div v-if="newField.isCalculated && newField.type === 'money'" class="mb-3">
              <div class="row">
                <div class="col-md-6">
                  <label for="fieldUnit" class="form-label">Einheit *</label>
                  <input 
                    v-model="newField.options.unit"
                    type="text" 
                    class="form-control"
                    id="fieldUnit"
                    placeholder="z.B. km, Stunde, Stück"
                    required
                  >
                </div>
                <div class="col-md-6">
                  <label for="fieldPricePerUnit" class="form-label">Preis pro Einheit *</label>
                  <input 
                    v-model.number="newField.options.pricePerUnit"
                    type="number" 
                    class="form-control"
                    id="fieldPricePerUnit"
                    step="0.01"
                    placeholder="0.30"
                    required
                  >
                </div>
              </div>
            </div>
            <!-- Min/Max Values for Financial Fields -->
            <div v-if="newField.category === 'financial'" class="mb-3">
              <label class="form-label">Wertbereich</label>
              <div class="row">
                <div class="col-md-6">
                  <label for="fieldMinValue" class="form-label">Mindestwert</label>
                  <input
                    v-model.number="newField.validation.min"
                    type="number"
                    class="form-control"
                    id="fieldMinValue"
                    step="0.01"
                    placeholder="z.B. 0.00"
                  />
                  <div class="form-text">Mindestwert für dieses Feld</div>
                </div>
                <div class="col-md-6">
                  <label for="fieldMaxValue" class="form-label">Höchstwert</label>
                  <input
                    v-model.number="newField.validation.max"
                    type="number"
                    class="form-control"
                    id="fieldMaxValue"
                    step="0.01"
                    placeholder="z.B. 9999.99"
                  />
                  <div class="form-text">Höchstwert für dieses Feld</div>
                </div>
              </div>
            </div>

            <!-- Steuersatz-Optionen für Geldbetrag-Felder -->
            <div v-if="newField.type === 'money'" class="mb-3">
              <div class="form-check">
                <input 
                  id="fieldIncludeTaxRate"
                  v-model="newField.options.includeTaxRate"
                  type="checkbox" 
                  class="form-check-input"
                >
                <label class="form-check-label" for="fieldIncludeTaxRate">
                  Steuersatz verwenden
                </label>
                <div class="form-text">
                  Ermöglicht die Auswahl eines Steuersatzes für dieses Feld
                </div>
              </div>

              <div v-if="newField.options.includeTaxRate" class="mt-3">
                <label class="form-label">Steuersatz-Auswahl</label>
                <select 
                  v-model="newField.options.taxRateSelectionMode"
                  class="form-select"
                >
                  <option value="user_choice">Benutzer wählt Steuersatz</option>
                  <option value="fixed">Fester Steuersatz</option>
                  <option value="none">Kein Steuersatz</option>
                </select>
                <div class="form-text">
                  <strong>Benutzer wählt:</strong> Der Benutzer kann aus verfügbaren Steuersätzen wählen<br>
                  <strong>Fester Steuersatz:</strong> Ein spezifischer Steuersatz wird automatisch angewendet<br>
                  <strong>Kein Steuersatz:</strong> Das Feld wird ohne Steuersatz behandelt
                </div>

                <!-- Auswahl der verfügbaren Steuersätze (nur bei user_choice) -->
                <div v-if="newField.options.taxRateSelectionMode === 'user_choice'" class="mt-3">
                  <label class="form-label">Verfügbare Steuersätze</label>
                  <div v-if="getSelectableTaxRates(newField.options).length === 0" class="text-muted small">Keine Steuersätze gefunden</div>
                  <div 
                    v-for="tr in getSelectableTaxRates(newField.options)" 
                    :key="`new-tr-${tr.id}`" 
                    class="form-check"
                  >
                    <input 
                      class="form-check-input"
                      type="checkbox"
                      :value="tr.id"
                      v-model="newField.options.availableTaxRates"
                      :id="`new-available-tr-${tr.id}`"
                    >
                    <label class="form-check-label" :for="`new-available-tr-${tr.id}`">
                      {{ tr.displayName }} ({{ tr.rate }}%)
                    </label>
                  </div>
                  <div class="mt-3">
                    <label class="form-label">Standard-Steuersatz</label>
                    <select
                      v-model="newField.options.selectedTaxRateId"
                      class="form-select"
                      :disabled="Array.isArray(newField.options.availableTaxRates) && newField.options.availableTaxRates.length === 0"
                    >
                      <option value="">-- Kein Standard --</option>
                      <option
                        v-for="tr in getDefaultTaxRateOptions(newField.options)"
                        :key="`new-default-tr-${tr.id}`"
                        :value="tr.id"
                      >
                        {{ tr.displayName }} ({{ tr.rate }}%)
                      </option>
                    </select>
                    <div class="form-text">
                      Wähle optional einen vorbelegten Steuersatz. Er muss zu den aktivierten Steuersätzen gehören.
                    </div>
                  </div>
                </div>
                
                <!-- Fixed tax rate selection -->
                <div v-if="newField.options.taxRateSelectionMode === 'fixed'" class="mt-3">
                  <label class="form-label">Festen Steuersatz wählen</label>
                  <select 
                    v-model="newField.options.selectedTaxRateId"
                    class="form-select"
                  >
                    <option value="">-- Steuersatz auswählen --</option>
                    <option 
                      v-for="tr in getSelectableTaxRates(newField.options)" 
                      :key="tr.id" 
                      :value="tr.id"
                    >
                      {{ tr.displayName }} ({{ tr.rate }}%)
                    </option>
                  </select>
                </div>
              </div>
            </div>
            
            <!-- Auswahloptionen für Select-Felder -->
            <div v-if="newField.type === 'select'" class="mb-3">
              <label for="fieldSelectOptions" class="form-label">Auswahloptionen *</label>
              <textarea 
                id="fieldSelectOptions"
                v-model="newField.selectOptions"
                class="form-control"
                rows="4"
                placeholder="Eine Option pro Zeile&#10;z.B.:&#10;Option 1&#10;Option 2&#10;Option 3"
                required
              ></textarea>
              <div class="form-text">
                Gib eine Auswahloption pro Zeile ein. Diese werden als Dropdown-Liste angezeigt.
              </div>
            </div>

            <!-- Repeater Field Configuration -->
            <div v-if="newField.type === 'repeater'" class="mb-3">
              <label class="form-label">Repeater-Konfiguration</label>
              <div class="alert alert-info">
                <font-awesome-icon icon="info-circle" class="me-2" />
                <strong>Repeater-Feld:</strong> Ermöglicht das Hinzufügen mehrerer Positionen mit folgenden Feldern:
                <ul class="mb-0 mt-2">
                  <li>Anzahl (Zahlenfeld)</li>
                  <li>Einheit (Textfeld, optional)</li>
                  <li>Beschreibung (Textfeld)</li>
                  <li>Einzelpreis (Geldbetrag)</li>
                  <li>Steuersatz (optional)</li>
                </ul>
              </div>
              
              <div class="row">
                <div class="col-md-6">
                  <label for="repeaterMinItems" class="form-label">Mindestanzahl Positionen</label>
                  <input
                    v-model.number="newField.options.repeaterMinItems"
                    type="number"
                    class="form-control"
                    id="repeaterMinItems"
                    min="0"
                    placeholder="0"
                  />
                  <div class="form-text">Mindestanzahl der Positionen (0 = keine Mindestanzahl)</div>
                </div>
                <div class="col-md-6">
                  <label for="repeaterMaxItems" class="form-label">Höchstanzahl Positionen</label>
                  <input
                    v-model.number="newField.options.repeaterMaxItems"
                    type="number"
                    class="form-control"
                    id="repeaterMaxItems"
                    min="1"
                    placeholder="10"
                  />
                  <div class="form-text">Höchstanzahl der Positionen (leer = unbegrenzt)</div>
                </div>
              </div>
              
              <div class="mt-3">
                <div class="form-check">
                  <input
                    v-model="newField.options.repeaterIncludeTaxRate"
                    class="form-check-input"
                    type="checkbox"
                    id="repeaterIncludeTaxRate"
                  />
                  <label class="form-check-label" for="repeaterIncludeTaxRate">
                    Steuersatz-Auswahl für Einzelpreise
                  </label>
                  <div class="form-text">
                    Ermöglicht die Auswahl eines Steuersatzes für jeden Einzelpreis
                  </div>
                </div>
              </div>
              
              <div v-if="newField.options.repeaterIncludeTaxRate" class="mt-3">
                <label class="form-label">Steuersatz-Auswahl</label>
                <select
                  v-model="newField.options.repeaterTaxRateSelectionMode"
                  class="form-select"
                >
                  <option value="user_choice">Benutzer wählt Steuersatz</option>
                  <option value="fixed">Fester Steuersatz</option>
                  <option value="none">Kein Steuersatz</option>
                </select>
                <div class="form-text">
                  <strong>Benutzer wählt:</strong> Der Benutzer kann aus verfügbaren Steuersätzen wählen<br>
                  <strong>Fester Steuersatz:</strong> Ein spezifischer Steuersatz wird automatisch angewendet<br>
                  <strong>Kein Steuersatz:</strong> Die Positionen werden ohne Steuersatz behandelt
                </div>

                <!-- Auswahl der verfügbaren Steuersätze (nur bei user_choice) -->
                <div v-if="newField.options.repeaterTaxRateSelectionMode === 'user_choice'" class="mt-3">
                  <label class="form-label">Verfügbare Steuersätze</label>
                  <div
                    v-for="taxRate in taxRates.filter((t) => t.isActive)"
                    :key="taxRate.id"
                    class="form-check"
                  >
                    <input
                      :value="taxRate.id"
                      v-model="newField.options.repeaterAvailableTaxRates"
                      class="form-check-input"
                      type="checkbox"
                      :id="`repeaterTaxRate-${taxRate.id}`"
                    />
                    <label class="form-check-label" :for="`repeaterTaxRate-${taxRate.id}`">
                      {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                    </label>
                  </div>
                </div>
                
                <!-- Fixed tax rate selection -->
                <div v-if="newField.options.repeaterTaxRateSelectionMode === 'fixed'" class="mt-3">
                  <label class="form-label">Festen Steuersatz wählen</label>
                  <select
                    v-model="newField.options.repeaterSelectedTaxRateId"
                    class="form-select"
                  >
                    <option value="">-- Steuersatz auswählen --</option>
                    <option
                      v-for="taxRate in taxRates.filter((t) => t.isActive)"
                      :key="taxRate.id"
                      :value="taxRate.id"
                    >
                      {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button 
            type="button" 
            class="btn btn-secondary" 
            @click="showCreateFieldModal = false"
          >
            Abbrechen
          </button>
          <button 
            type="button" 
            class="btn btn-primary"
            @click="createField"
            :disabled="creatingField"
          >
            <span v-if="creatingField" class="spinner-border spinner-border-sm me-2"></span>
            {{ creatingField ? 'Erstellen...' : 'Erstellen' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Edit Field Modal -->
  <div 
    v-if="showEditFieldModal"
    class="modal show"
    tabindex="-1"
    aria-labelledby="editFieldModalLabel"
    aria-hidden="false"
    style="display: block;"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="editFieldModalLabel">
            <font-awesome-icon icon="edit" class="me-2" />
            Feld bearbeiten
          </h5>
          <button 
            type="button" 
            class="btn-close" 
            @click="showEditFieldModal = false"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="updateField">
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editFieldName" class="form-label">Interner Name *</label>
                  <input 
                    id="editFieldName"
                    v-model="editingField.name"
                    type="text" 
                    :disabled="editingField.undeletable"
                    class="form-control"
                    required
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editFieldDisplayName" class="form-label">Anzeigename *</label>
                  <input 
                    id="editFieldDisplayName"
                    v-model="editingField.displayName"
                    type="text" 
                    class="form-control"
                    required
                  >
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editFieldType" class="form-label">Feldtyp *</label>
                  <select 
                    id="editFieldType"
                    v-model="editingField.type"
                    :disabled="editingField.undeletable"
                    class="form-select"
                    required
                  >
                    <option v-if="editingField.category !== 'financial'" value="text">Text</option>
                    <option value="number">Zahl</option>
                    <option v-if="editingField.category === 'receipt'" value="location">Ort</option>
                    <option v-if="editingField.category !== 'financial'" value="date">Datum</option>
                    <option v-if="editingField.category !== 'financial'" value="textarea">Textbereich</option>
                    <option v-if="editingField.category !== 'financial'" value="file">Datei</option>
                    <option v-if="editingField.category !== 'financial'" value="select">Auswahl</option>
                    <option v-if="editingField.category === 'financial'" value="money">Geldbetrag</option>
                    <option v-if="editingField.category === 'receipt'" value="repeater">Repeater (Wiederholbare Positionen)</option>
                  </select>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editFieldCategory" class="form-label">Kategorie *</label>
                  <select 
                    id="editFieldCategory"
                    v-model="editingField.category"
                    :disabled="editingField.undeletable"
                    class="form-select"
                    required
                  >
                    <option value="receipt">Quittung</option>
                    <option value="financial">Finanziell</option>
                    <option value="personal">Persönlich</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="row" v-if="getFieldGroups(editingField.category).length > 0">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editFieldGroup" class="form-label">Gruppe</label>
                  <select
                    id="editFieldGroup"
                    v-model="editingField.options.groupId"
                    class="form-select"
                  >
                    <option :value="null">Keine Gruppe</option>
                    <option
                      v-for="group in getFieldGroups(editingField.category)"
                      :key="group.id"
                      :value="group.id"
                    >
                      {{ group.name }}
                    </option>
                  </select>
                  <div class="form-text">
                    Ändere die Gruppenzuordnung für dieses Feld.
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editFieldPlaceholder" class="form-label">Placeholder</label>
                  <input 
                    id="editFieldPlaceholder"
                    v-model="editingField.placeholder"
                    type="text" 
                    class="form-control"
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editFieldDefaultValue" class="form-label">Standardwert</label>
                  <input 
                    id="editFieldDefaultValue"
                    v-model="editingField.defaultValue"
                    type="text" 
                    class="form-control"
                  >
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label for="editFieldDescription" class="form-label">Beschreibung</label>
              <textarea 
                id="editFieldDescription"
                v-model="editingField.description"
                class="form-control"
                rows="3"
              ></textarea>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-check">
                  <input 
                    id="editFieldRequired"
                    v-model="editingField.required"
                    type="checkbox" 
                    class="form-check-input"
                    :disabled="editingField.undeletable && (editingField.type === 'money' || editingField.category === 'financial')"
                  >
                  <label class="form-check-label" for="editFieldRequired">
                    Erforderlich
                  </label>
                  <div v-if="editingField.undeletable && (editingField.type === 'money' || editingField.category === 'financial')" class="form-text text-muted">
                    <small>Finanzielle Fixed-Felder können nicht als optional markiert werden</small>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-check">
                  <input 
                    id="editFieldAllowPrefill"
                    v-model="editingField.allowPrefill"
                    type="checkbox"
                    class="form-check-input"
                  >
                  <label class="form-check-label" for="editFieldAllowPrefill">
                    Im Profil vorausfüllbar
                  </label>
                  <div class="form-text text-muted">
                    <small>Ermöglicht Mitgliedern, dieses Feld im Profil vorzubereiten.</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Calculated Field Options (only for money type) -->
            <div v-if="editingField.type === 'money'" class="mb-3">
              <div class="form-check">
                <input 
                  v-model="editingField.isCalculated"
                  class="form-check-input"
                  type="checkbox"
                  id="editFieldIsCalculated"
                  :disabled="editingField.undeletable"
                >
                <label class="form-check-label" for="editFieldIsCalculated">
                  Berechnetes Feld (Preis pro Einheit)
                </label>
                <div class="form-text">
                  Das Feld wird automatisch basierend auf einer Menge und einem Preis pro Einheit berechnet
                  <div v-if="editingField.undeletable" class="text-muted">
                    <small>Fixed-Felder können nicht als berechnet markiert werden</small>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Per Unit Calculation Configuration -->
            <div v-if="editingField.isCalculated && editingField.type === 'money'" class="mb-3">
              <div class="row">
                <div class="col-md-6">
                  <label for="editFieldUnit" class="form-label">Einheit *</label>
                  <input 
                    v-model="editingField.options.unit"
                    type="text" 
                    class="form-control"
                    id="editFieldUnit"
                    placeholder="z.B. km, Stunde, Stück"
                    :disabled="editingField.undeletable"
                    required
                  >
                </div>
                <div class="col-md-6">
                  <label for="editFieldPricePerUnit" class="form-label">Preis pro Einheit *</label>
                  <input 
                    v-model.number="editingField.options.pricePerUnit"
                    type="number" 
                    class="form-control"
                    id="editFieldPricePerUnit"
                    step="0.01"
                    placeholder="0.30"
                    :disabled="editingField.undeletable"
                    required
                  >
                </div>
              </div>
            </div>

            <!-- Min/Max Values for Financial Fields -->
            <div v-if="editingField.category === 'financial'" class="mb-3">
              <label class="form-label">Wertbereich</label>
              <div class="row">
                <div class="col-md-6">
                  <label for="editFieldMinValue" class="form-label">Mindestwert</label>
                  <input
                    v-model.number="editingField.validation.min"
                    type="number"
                    class="form-control"
                    id="editFieldMinValue"
                    step="0.01"
                    placeholder="z.B. 0.00"
                  />
                  <div class="form-text">Mindestwert für dieses Feld</div>
                </div>
                <div class="col-md-6">
                  <label for="editFieldMaxValue" class="form-label">Höchstwert</label>
                  <input
                    v-model.number="editingField.validation.max"
                    type="number"
                    class="form-control"
                    id="editFieldMaxValue"
                    step="0.01"
                    placeholder="z.B. 9999.99"
                  />
                  <div class="form-text">Höchstwert für dieses Feld</div>
                </div>
              </div>
            </div>

            <!-- Steuersatz-Optionen für Geldbetrag-Felder -->
            <div v-if="editingField.type === 'money'" class="mb-3">
              <div class="form-check">
                <input 
                  id="editFieldIncludeTaxRate"
                  v-model="editingField.options.includeTaxRate"
                  type="checkbox" 
                  class="form-check-input"
                >
                <label class="form-check-label" for="editFieldIncludeTaxRate">
                  Steuersatz verwenden
                </label>
                <div class="form-text">
                  Ermöglicht die Auswahl eines Steuersatzes für dieses Feld
                </div>
              </div>

              <div v-if="editingField.options.includeTaxRate" class="mt-3">
                <label class="form-label">Steuersatz-Auswahl</label>
                <select 
                  v-model="editingField.options.taxRateSelectionMode"
                  class="form-select"
                >
                  <option value="user_choice">Benutzer wählt Steuersatz</option>
                  <option value="fixed">Fester Steuersatz</option>
                  <option value="none">Kein Steuersatz</option>
                </select>
                <div class="form-text">
                  <strong>Benutzer wählt:</strong> Der Benutzer kann aus verfügbaren Steuersätzen wählen<br>
                  <strong>Fester Steuersatz:</strong> Ein spezifischer Steuersatz wird automatisch angewendet<br>
                  <strong>Kein Steuersatz:</strong> Das Feld wird ohne Steuersatz behandelt
                </div>

                <!-- Auswahl der verfügbaren Steuersätze (nur bei user_choice) -->
                <div v-if="editingField.options.taxRateSelectionMode === 'user_choice'" class="mt-3">
                  <label class="form-label">Verfügbare Steuersätze</label>
                  <div v-if="getSelectableTaxRates(editingField.options).length === 0" class="text-muted small">Keine Steuersätze gefunden</div>
                  <div 
                    v-for="tr in getSelectableTaxRates(editingField.options)" 
                    :key="`edit-tr-${tr.id}`" 
                    class="form-check"
                  >
                    <input 
                      class="form-check-input"
                      type="checkbox"
                      :value="tr.id"
                      v-model="editingField.options.availableTaxRates"
                      :id="`edit-available-tr-${tr.id}`"
                    >
                    <label class="form-check-label" :for="`edit-available-tr-${tr.id}`">
                      {{ tr.displayName }} ({{ tr.rate }}%)
                    </label>
                  </div>
                  <div class="mt-3">
                    <label class="form-label">Standard-Steuersatz</label>
                    <select
                      v-model="editingField.options.selectedTaxRateId"
                      class="form-select"
                      :disabled="Array.isArray(editingField.options.availableTaxRates) && editingField.options.availableTaxRates.length === 0"
                    >
                      <option value="">-- Kein Standard --</option>
                      <option
                        v-for="tr in getDefaultTaxRateOptions(editingField.options)"
                        :key="`edit-default-tr-${tr.id}`"
                        :value="tr.id"
                      >
                        {{ tr.displayName }} ({{ tr.rate }}%)
                      </option>
                    </select>
                    <div class="form-text">
                      Wähle optional einen vorbelegten Steuersatz. Er muss zu den aktivierten Steuersätzen gehören.
                    </div>
                  </div>
                </div>

                <!-- Fixed tax rate selection -->
                <div v-if="editingField.options.taxRateSelectionMode === 'fixed'" class="mt-3">
                  <label class="form-label">Festen Steuersatz wählen</label>
                  <select 
                    v-model="editingField.options.selectedTaxRateId"
                    class="form-select"
   
                  >
                    <option value="">-- Steuersatz auswählen --</option>
                    <option 
                      v-for="tr in getSelectableTaxRates(editingField.options)" 
                      :key="tr.id" 
                      :value="tr.id"
                    >
                      {{ tr.displayName }} ({{ tr.rate }}%)
                    </option>
                  </select>
                </div>
              </div>
            </div>
            
            <!-- Auswahloptionen für Select-Felder -->
            <div v-if="editingField.type === 'select'" class="mb-3">
              <label for="editFieldSelectOptions" class="form-label">Auswahloptionen *</label>
              <textarea 
                id="editFieldSelectOptions"
                v-model="editingField.selectOptions"
                class="form-control"
                rows="4"
                placeholder="Eine Option pro Zeile&#10;z.B.:&#10;Option 1&#10;Option 2&#10;Option 3"
                required
              ></textarea>
              <div class="form-text">
                Gib eine Auswahloption pro Zeile ein. Diese werden als Dropdown-Liste angezeigt.
              </div>
            </div>

            <!-- Repeater Field Configuration -->
            <div v-if="editingField.type === 'repeater'" class="mb-3">
              <label class="form-label">Repeater-Konfiguration</label>
              <div class="alert alert-info">
                <font-awesome-icon icon="info-circle" class="me-2" />
                <strong>Repeater-Feld:</strong> Ermöglicht das Hinzufügen mehrerer Positionen mit folgenden Feldern:
                <ul class="mb-0 mt-2">
                  <li>Anzahl (Zahlenfeld)</li>
                  <li>Einheit (Textfeld, optional)</li>
                  <li>Beschreibung (Textfeld)</li>
                  <li>Einzelpreis (Geldbetrag)</li>
                  <li>Steuersatz (optional)</li>
                </ul>
              </div>
              
              <div class="row">
                <div class="col-md-6">
                  <label for="editRepeaterMinItems" class="form-label">Mindestanzahl Positionen</label>
                  <input
                    v-model.number="editingField.options.repeaterMinItems"
                    type="number"
                    class="form-control"
                    id="editRepeaterMinItems"
                    min="0"
                    placeholder="0"
                  />
                  <div class="form-text">Mindestanzahl der Positionen (0 = keine Mindestanzahl)</div>
                </div>
                <div class="col-md-6">
                  <label for="editRepeaterMaxItems" class="form-label">Höchstanzahl Positionen</label>
                  <input
                    v-model.number="editingField.options.repeaterMaxItems"
                    type="number"
                    class="form-control"
                    id="editRepeaterMaxItems"
                    min="1"
                    placeholder="10"
                  />
                  <div class="form-text">Höchstanzahl der Positionen (leer = unbegrenzt)</div>
                </div>
              </div>
              
              <div class="mt-3">
                <div class="form-check">
                  <input
                    v-model="editingField.options.repeaterIncludeTaxRate"
                    class="form-check-input"
                    type="checkbox"
                    id="editRepeaterIncludeTaxRate"
                  />
                  <label class="form-check-label" for="editRepeaterIncludeTaxRate">
                    Steuersatz-Auswahl für Einzelpreise
                  </label>
                  <div class="form-text">
                    Ermöglicht die Auswahl eines Steuersatzes für jeden Einzelpreis
                  </div>
                </div>
              </div>
              
              <div v-if="editingField.options.repeaterIncludeTaxRate" class="mt-3">
                <label class="form-label">Steuersatz-Auswahl</label>
                <select
                  v-model="editingField.options.repeaterTaxRateSelectionMode"
                  class="form-select"
                >
                  <option value="user_choice">Benutzer wählt Steuersatz</option>
                  <option value="fixed">Fester Steuersatz</option>
                  <option value="none">Kein Steuersatz</option>
                </select>
                <div class="form-text">
                  <strong>Benutzer wählt:</strong> Der Benutzer kann aus verfügbaren Steuersätzen wählen<br>
                  <strong>Fester Steuersatz:</strong> Ein spezifischer Steuersatz wird automatisch angewendet<br>
                  <strong>Kein Steuersatz:</strong> Die Positionen werden ohne Steuersatz behandelt
                </div>

                <!-- Auswahl der verfügbaren Steuersätze (nur bei user_choice) -->
                <div v-if="editingField.options.repeaterTaxRateSelectionMode === 'user_choice'" class="mt-3">
                  <label class="form-label">Verfügbare Steuersätze</label>
                  <div
                    v-for="taxRate in taxRates.filter((t) => t.isActive)"
                    :key="taxRate.id"
                    class="form-check"
                  >
                    <input
                      :value="taxRate.id"
                      v-model="editingField.options.repeaterAvailableTaxRates"
                      class="form-check-input"
                      type="checkbox"
                      :id="`editRepeaterTaxRate-${taxRate.id}`"
                    />
                    <label class="form-check-label" :for="`editRepeaterTaxRate-${taxRate.id}`">
                      {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                    </label>
                  </div>
                </div>
                
                <!-- Fixed tax rate selection -->
                <div v-if="editingField.options.repeaterTaxRateSelectionMode === 'fixed'" class="mt-3">
                  <label class="form-label">Festen Steuersatz wählen</label>
                  <select
                    v-model="editingField.options.repeaterSelectedTaxRateId"
                    class="form-select"
                  >
                    <option value="">-- Steuersatz auswählen --</option>
                    <option
                      v-for="taxRate in taxRates.filter((t) => t.isActive)"
                      :key="taxRate.id"
                      :value="taxRate.id"
                    >
                      {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button 
            type="button" 
            class="btn btn-secondary" 
            @click="showEditFieldModal = false"
          >
            Abbrechen
          </button>
          <button 
            type="button" 
            class="btn btn-primary"
            @click="updateField"
            :disabled="updatingField"
          >
            <span v-if="updatingField" class="spinner-border spinner-border-sm me-2"></span>
            {{ updatingField ? 'Aktualisieren...' : 'Aktualisieren' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Backdrops -->
  <div v-if="showEditNameModal" class="modal-backdrop fade show" @click="showEditNameModal = false"></div>
  <div v-if="showTitleSettingsModal" class="modal-backdrop fade show" @click="closeTitleSettingsModal"></div>
  <div v-if="showFieldsModal" class="modal-backdrop fade show" @click="showFieldsModal = false"></div>
  <div v-if="showCreateFieldModal" class="modal-backdrop fade show" @click="showCreateFieldModal = false"></div>
  <div v-if="showEditFieldModal" class="modal-backdrop fade show" @click="showEditFieldModal = false"></div>

<!-- Assign Template Modal -->
<AssignTemplateModal 
  :team-id="$route.params.id"
  :show-modal="showAssignTemplateModal"
  @template-assigned="onTemplateAssigned"
  @modal-closed="showAssignTemplateModal = false"
/>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useTeamsStore } from '@/stores/teams'
import { templateService } from '@/api/templates'
import { showSuccess, showError, showConfirmDialog } from '@/services/notification'
import AssignTemplateModal from '@/components/teams/AssignTemplateModal.vue'
import BackButton from '@/components/common/BackButton.vue'

const router = useRouter()
const route = useRoute()
const teamsStore = useTeamsStore()

const { selectedTeam, loading, error } = storeToRefs(teamsStore)

const teamTemplates = ref([])
const updatingName = ref(false)
const settingAsDefault = ref(false)
const updatingSignature = ref(false)
const showAssignTemplateModal = ref(false)
const showEditNameModal = ref(false)
const showFieldsModal = ref(false)
const showCreateFieldModal = ref(false)
const showEditFieldModal = ref(false)
const showTitleSettingsModal = ref(false)
const creatingField = ref(false)
const updatingField = ref(false)
const savingTitleSettings = ref(false)
const selectedTemplateForFields = ref(null)
const selectedTemplateForTitle = ref(null)
const titlePatternInput = ref(null)
const titleSettingsForm = ref({
  pattern: '',
  pdfTitle: '',
  preview: ''
})
const taxRates = ref([])


const orderMode = ref(null) 
const savingOrder = ref(false)
const draggedField = ref(null)
const draggedCategory = ref(null)

const FIELD_GROUP_CATEGORIES = ['receipt', 'financial', 'personal']
const DEFAULT_GROUP_COLORS = ['#D71021', '#0D6EFD', '#6610F2', '#198754', '#FD7E14', '#6C757D', '#0DCAF0', '#6F42C1']
const fieldGroupsState = ref({
  receipt: [],
  financial: [],
  personal: []
})
const fieldGroupsDirty = ref(false)
const savingFieldGroups = ref(false)
const updatingFieldGroupId = ref(null)


const getSelectableTaxRates = (options) => {
  if (options?.resolvedTaxRates && options.resolvedTaxRates.length > 0) {
    return options.resolvedTaxRates.filter(tr => tr.isActive !== false)
  }
  return (taxRates.value || []).filter(tr => tr.isActive)
}

const generateGroupId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `grp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

const getFieldGroups = (category) => {
  if (!FIELD_GROUP_CATEGORIES.includes(category)) {
    return []
  }
  return fieldGroupsState.value[category] || []
}

const markFieldGroupsDirty = () => {
  fieldGroupsDirty.value = true
}

const initializeFieldGroups = () => {
  const rawGroups = selectedTemplateForFields.value?.settings?.fieldGroups || {}
  const cloneCategory = (category) => {
    const groups = Array.isArray(rawGroups?.[category]) ? rawGroups[category] : []
    return groups
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((group, index) => ({
        id: group.id || generateGroupId(),
        name: group.name || '',
        color: group.color || '#E9ECEF',
        order: index
      }))
  }

  fieldGroupsState.value = {
    receipt: cloneCategory('receipt'),
    financial: cloneCategory('financial'),
    personal: cloneCategory('personal')
  }
  fieldGroupsDirty.value = false
}

const addFieldGroup = (category) => {
  if (!FIELD_GROUP_CATEGORIES.includes(category)) {
    return
  }

  const existing = getFieldGroups(category)
  const color = DEFAULT_GROUP_COLORS[existing.length % DEFAULT_GROUP_COLORS.length] || '#E9ECEF'
  const nextName = existing.length === 0 ? 'Neue Gruppe' : `Neue Gruppe ${existing.length + 1}`
  const newGroup = {
    id: generateGroupId(),
    name: nextName,
    color,
    order: existing.length
  }

  fieldGroupsState.value = {
    ...fieldGroupsState.value,
    [category]: [...existing, newGroup]
  }
  markFieldGroupsDirty()
}

const moveFieldGroup = (category, index, direction) => {
  if (!FIELD_GROUP_CATEGORIES.includes(category)) {
    return
  }

  const groups = [...getFieldGroups(category)]
  const targetIndex = index + direction
  if (targetIndex < 0 || targetIndex >= groups.length) {
    return
  }

  const [movedGroup] = groups.splice(index, 1)
  groups.splice(targetIndex, 0, movedGroup)

  const reordered = groups.map((group, idx) => ({
    ...group,
    order: idx
  }))

  fieldGroupsState.value = {
    ...fieldGroupsState.value,
    [category]: reordered
  }
  markFieldGroupsDirty()
}

const getGroupById = (category, groupId) => {
  if (!groupId) {
    return null
  }
  return getFieldGroups(category).find((group) => group.id === groupId) || null
}

const getGroupColor = (category, groupId) => {
  const group = getGroupById(category, groupId)
  return group?.color || '#E9ECEF'
}

const getGroupName = (category, groupId) => {
  const group = getGroupById(category, groupId)
  return group?.name || ''
}

const buildFieldGroupsPayload = () => {
  const payload = {}

  FIELD_GROUP_CATEGORIES.forEach((category) => {
    const groups = getFieldGroups(category).map((group, index) => ({
      id: group.id,
      name: (group.name || '').trim(),
      color: group.color || '#E9ECEF',
      order: index
    }))

    if (groups.length > 0) {
      payload[category] = groups
    }
  })

  return Object.keys(payload).length > 0 ? payload : null
}

const saveFieldGroups = async () => {
  if (!selectedTemplateForFields.value || !fieldGroupsDirty.value) {
    return
  }

  const payload = buildFieldGroupsPayload()

  if (payload) {
    const hasEmptyNames = Object.values(payload).some((groups) =>
      groups.some((group) => !group.name || group.name.trim().length === 0)
    )
    if (hasEmptyNames) {
      showError('Bitte vergib Namen für alle Gruppen.')
      return
    }
  }

  savingFieldGroups.value = true
  try {
    await templateService.updateTeamTemplateFieldGroups(
      selectedTemplateForFields.value.id,
      payload
    )

    await loadTeamTemplates()
    initializeFieldGroups()
    fieldGroupsDirty.value = false
    showSuccess('Feld-Gruppen gespeichert')
  } catch (err) {
    console.error('Error saving field groups:', err)
    showError(err.response?.data?.error || 'Fehler beim Speichern der Feld-Gruppen')
  } finally {
    savingFieldGroups.value = false
  }
}

const getDefaultTaxRateOptions = (options) => {
  const selectable = getSelectableTaxRates(options)
  if (!options) {
    return selectable
  }

  if (options.taxRateSelectionMode !== 'user_choice') {
    return selectable
  }

  const availableIds = Array.isArray(options.availableTaxRates) ? options.availableTaxRates : []
  if (availableIds.length === 0) {
    return selectable
  }

  const allowed = new Set(availableIds)
  return selectable.filter((tr) => allowed.has(tr.id))
}

const ensureValidDefaultTaxRate = (options) => {
  if (!options) return

  if (!options.includeTaxRate) {
    options.selectedTaxRateId = ''
    return
  }

  if (options.taxRateSelectionMode === 'user_choice') {
    const hasAvailable =
      Array.isArray(options.availableTaxRates) && options.availableTaxRates.length > 0
    const available = hasAvailable ? options.availableTaxRates : null

    if (
      hasAvailable &&
      options.selectedTaxRateId &&
      !available.includes(options.selectedTaxRateId)
    ) {
      options.selectedTaxRateId = ''
    }
    return
  }

  if (options.taxRateSelectionMode !== 'fixed') {
    options.selectedTaxRateId = ''
  }
}



const activeTab = ref('receipt') 
const isProTeam = computed(() => ['pro', 'ultimate'].includes(selectedTeam.value?.status))
const editNameForm = ref({
  templateId: null,
  customName: '',
  originalName: '',
  isDefault: false,
  needsSignature: true
})
const newField = ref({
  name: '',
  displayName: '',
  type: 'text',
  category: 'receipt',
  required: false,
  allowPrefill: false,
  placeholder: '',
  description: '',
  defaultValue: '',
  isCalculated: false,
  calculationFormula: '',
  selectOptions: '',
  validation: {
    min: null,
    max: null
  },
  options: {
    includeTaxRate: false,
    availableTaxRates: [],
    unit: '',
    pricePerUnit: 0,
    dependsOn: '',
    taxRateSelectionMode: 'user_choice',
    selectedTaxRateId: '',
    resolvedTaxRates: [],
    calculationType: 'simple',
    // Repeater options
    repeaterMinItems: 0,
    repeaterMaxItems: null,
    repeaterIncludeTaxRate: false,
    repeaterTaxRateSelectionMode: 'user_choice',
    repeaterAvailableTaxRates: [],
    repeaterSelectedTaxRateId: '',
    groupId: null,
  }
})
const editingField = ref({
  name: '',
  displayName: '',
  type: 'text',
  category: 'receipt',
  required: false,
  allowPrefill: false,
  placeholder: '',
  description: '',
  defaultValue: '',
  isCalculated: false,
  calculationFormula: '',
  selectOptions: '',
  validation: {
    min: null,
    max: null
  },
  options: {
    includeTaxRate: false,
    availableTaxRates: [],
    unit: '',
    pricePerUnit: 0,
    dependsOn: '',
    taxRateSelectionMode: 'user_choice',
    selectedTaxRateId: '',
    resolvedTaxRates: [],
    calculationType: 'simple',
    // Repeater options
    repeaterMinItems: 0,
    repeaterMaxItems: null,
    repeaterIncludeTaxRate: false,
    repeaterTaxRateSelectionMode: 'user_choice',
    repeaterAvailableTaxRates: [],
    repeaterSelectedTaxRateId: '',
    groupId: null,
  }
})

const BUILTIN_TITLE_PLACEHOLDERS = [
  { key: 'receiptNumber', label: 'Quittungsnummer' },
  { key: 'teamName', label: 'Bereich' },
  { key: 'userFirstName', label: 'Erstellt von (Vorname)' },
  { key: 'userLastName', label: 'Erstellt von (Nachname)' },
  { key: 'receiptDate', label: 'Datum (formatiert)' },
  { key: 'receiptDateISO', label: 'Datum (ISO)' },
  { key: 'receiptLocation', label: 'Ort' },
  { key: 'amount', label: 'Betrag (formatiert)' },
  { key: 'amountRaw', label: 'Betrag (Rohwert)' },
  { key: 'templateCustomName', label: 'Design (individuell)' },
  { key: 'templateDisplayName', label: 'Design (Standardname)' },
  { key: 'templateName', label: 'Design (Systemname)' }
]

const onTemplateAssigned = async () => {
  await loadTeamTemplates()
  showSuccess('Template erfolgreich zugewiesen')
}

const openAssignTemplateModal = () => {
  showAssignTemplateModal.value = true
}

const loadTaxRates = async () => {
  try {
    if(editingField.value.options.resolvedTaxRates.length > 0) {
      taxRates.value = editingField.value.options.resolvedTaxRates
    } else {  
    
    const response = await templateService.getAllTaxRates(true)
    
    if (Array.isArray(response)) {
      taxRates.value = response
      editingField.value.options.resolvedTaxRates = response
    } else if (response && Array.isArray(response.data)) {
      taxRates.value = response.data
      editingField.value.options.resolvedTaxRates = response.data
    } else {
      taxRates.value = []
    }
  }
  } catch (err) {
    console.error('Error loading tax rates:', err)
    taxRates.value = []
  }
}


const goBack = () => {
  router.push({ name: 'teams-settings', params: { id: route.params.id } })
}

const canDeactivateTemplate = (teamTemplate) => {
  if (!teamTemplate) return false
  if (!teamTemplate.isDefault) return true
  if (isProTeam.value) return true

  const otherTemplates = teamTemplates.value.filter(tt => tt.id !== teamTemplate.id)
  return otherTemplates.length > 0
}

const getTemplateDisplayName = (teamTemplate) => {
  if (teamTemplate && teamTemplate.customName) {
    return teamTemplate.customName
  }
  
  return teamTemplate.template.displayName
}

const changeTab = (event) => {
  activeTab.value = event.target.value
}

const openEditNameModal = (teamTemplate) => {
  
 
  editNameForm.value = {
    templateId: teamTemplate.id,
    customName: teamTemplate?.customName || '',
    originalName: teamTemplate.template.displayName,
    isDefault: teamTemplate?.isDefault || false,
    needsSignature: teamTemplate?.needsSignature !== false
  }
  
  showEditNameModal.value = true
}


const saveCustomName = async () => {
  if (!editNameForm.value.templateId) return
  
  updatingName.value = true
  
  try {
    // Speichere sowohl den Namen als auch die needsSignature-Einstellung
    await Promise.all([
      templateService.updateTeamTemplateCustomName(
        editNameForm.value.templateId,
        editNameForm.value.customName || null
      ),
      templateService.updateTeamTemplateNeedsSignature(
        editNameForm.value.templateId,
        editNameForm.value.needsSignature
      )
    ])
    
    
    await loadTeamTemplates()
    
    showSuccess('Template-Einstellungen erfolgreich aktualisiert')
    
    
    showEditNameModal.value = false
  } catch (err) {
    console.error('Error updating template settings:', err)
    showError(err.response?.data?.error || 'Fehler beim Aktualisieren der Template-Einstellungen')
  } finally {
    updatingName.value = false
  }
}

const setAsDefault = async () => {
  if (!editNameForm.value.templateId) return
  
  
  const teamTemplate = teamTemplates.value.find(tt => tt.id === editNameForm.value.templateId)
  if (!teamTemplate) {
    showError('BereichTemplate nicht gefunden')
    return
  }
  
  const confirmed = await showConfirmDialog(
    'Standard-Template ändern',
    `Möchtest du "${editNameForm.value.originalName}" als neues Standard-Template für das Team festlegen?\n\nDas aktuelle Standard-Template wird dadurch ersetzt.`,
    'Als Standard setzen',
    'Abbrechen',
    {
      icon: 'question',
      confirmButtonColor: '#ffc107'
    }
  )
  
  if (!confirmed.isConfirmed) return
  
  settingAsDefault.value = true
  
  try {
    await templateService.setTeamTemplateDefault(route.params.id, teamTemplate.id)
    
    
    await loadTeamTemplates()
    
    showSuccess(`"${editNameForm.value.originalName}" wurde als Standard-Template festgelegt`)
    
    
    editNameForm.value.isDefault = true
  } catch (err) {
    console.error('Error setting template as default:', err)
    showError(err.response?.data?.error || 'Fehler beim Setzen des Standard-Templates')
  } finally {
    settingAsDefault.value = false
  }
}


const removeActivatedTemplate = async (teamTemplate) => {
  
  if (!canDeactivateTemplate(teamTemplate)) {
    showError('Standard-Template kann nicht gelöscht werden. Mindestens ein Template muss aktiv bleiben.')
    return
  }
  
  const confirmed = await showConfirmDialog(
    'Template löschen',
    `Möchtest du das Template "${getTemplateDisplayName(teamTemplate)}" wirklich löschen?\n\nWarnung: Alle zugehörigen Custom Fields werden ebenfalls gelöscht!`,
    'Löschen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#dc3545'
    }
  )
  if (!confirmed.isConfirmed) return
  
  try {
   await templateService.removeTeamTemplate(teamTemplate.id)
    
  
    await loadTeamTemplates()
    
    showSuccess('Template erfolgreich gelöscht')
  } catch (err) {
    console.error('Error removing template:', err)
    showError(err.response?.data?.error || 'Fehler beim Löschen des Templates')
  }
}

const loadTeamTemplates = async () => {
  try {
    const response = await templateService.getTeamTemplates(route.params.id)
    teamTemplates.value = response.data || []
    refreshSelectedTemplateForFields()
  } catch (err) {
    console.error('Error loading team templates:', err)
    
    teamTemplates.value = []
    refreshSelectedTemplateForFields()
  }
}


const refreshSelectedTemplateForFields = () => {
  if (selectedTemplateForFields.value) {
    const updatedFieldTemplate = teamTemplates.value.find(
      tt => tt.id === selectedTemplateForFields.value.id
    )
    if (updatedFieldTemplate) {
      selectedTemplateForFields.value = updatedFieldTemplate
    } else {
      selectedTemplateForFields.value = null
    }
  }

  if (selectedTemplateForTitle.value) {
    const updatedTitleTemplate = teamTemplates.value.find(
      tt => tt.id === selectedTemplateForTitle.value.id
    )
    if (updatedTitleTemplate) {
      selectedTemplateForTitle.value = updatedTitleTemplate
      updateTitlePreview()
    } else {
      selectedTemplateForTitle.value = null
      titleSettingsForm.value.pattern = ''
      titleSettingsForm.value.preview = ''
    }
  }
}

const goToDrafts = () => {
  router.push({ name: 'teams-template-drafts', params: { id: route.params.id } })
}

const goToDraftsForTemplate = (teamTemplate) => {

  router.push({ 
    name: 'teams-template-drafts', 
    params: { id: route.params.id },
    query: { teamTemplateId: teamTemplate.id }
  })
}

const goToFieldsForTemplate = (teamTemplate) => {
  
  selectedTemplateForFields.value = teamTemplate
  
  initializeFieldGroups()
  showFieldsModal.value = true
}

const goToFieldsFromEditModal = () => {
  
  const currentTemplate = teamTemplates.value.find(tt => tt.id === editNameForm.value.templateId)
  if (currentTemplate) {
    
    showEditNameModal.value = false
    
    selectedTemplateForFields.value = currentTemplate
    
    initializeFieldGroups()
    showFieldsModal.value = true
  }
}

const goToDraftsFromEditModal = () => {
  
  const currentTemplate = teamTemplates.value.find(tt => tt.id === editNameForm.value.templateId)
  if (currentTemplate) {
    
    showEditNameModal.value = false
    
    router.push({ 
      name: 'teams-template-drafts', 
      params: { id: route.params.id },
      query: { teamTemplateId: currentTemplate.id }
    })
  }
}

const titlePlaceholderOptions = computed(() => {
  const template = selectedTemplateForTitle.value
  const placeholders = [...BUILTIN_TITLE_PLACEHOLDERS]

  const seenKeys = new Set(placeholders.map(item => item.key))

  if (template?.template?.fields) {
    template.template.fields.forEach(field => {
      if (!field?.name) return
      if (seenKeys.has(field.name)) return
      placeholders.push({
        key: field.name,
        label: field.displayName || field.name
      })
      seenKeys.add(field.name)
    })
  }

  return placeholders
})

const updateTitlePreview = () => {
  const pattern = titleSettingsForm.value.pattern || ''
  if (!pattern) {
    titleSettingsForm.value.preview = ''
    return
  }

  const placeholderMap = new Map(
    titlePlaceholderOptions.value.map(option => [option.key, option.label])
  )

  const preview = pattern.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, key) => {
    const normalizedKey = key.trim()
    if (!normalizedKey) return ''
    return placeholderMap.get(normalizedKey) || `[${normalizedKey}]`
  })

  titleSettingsForm.value.preview = preview
}

const openTitleSettingsModal = async (teamTemplate) => {
  if (!teamTemplate) return

  selectedTemplateForTitle.value = teamTemplate
  const existingPattern = teamTemplate?.settings?.title?.pattern || ''
  const existingPdfTitle = teamTemplate?.settings?.title?.pdfTitle || ''
  titleSettingsForm.value.pattern = existingPattern
  titleSettingsForm.value.pdfTitle = existingPdfTitle
  updateTitlePreview()

  showTitleSettingsModal.value = true
  await nextTick()
  if (titlePatternInput.value) {
    const input = titlePatternInput.value
    const length = input.value?.length ?? 0
    input.focus()
    input.setSelectionRange(length, length)
  }
}

const insertPlaceholder = (placeholderKey) => {
  const placeholder = `{{${placeholderKey}}}`
  const input = titlePatternInput.value

  const currentValue = titleSettingsForm.value.pattern || ''

  if (input) {
    const start = input.selectionStart ?? currentValue.length
    const end = input.selectionEnd ?? currentValue.length
    titleSettingsForm.value.pattern =
      currentValue.slice(0, start) + placeholder + currentValue.slice(end)

    nextTick(() => {
      const cursor = start + placeholder.length
      input.focus()
      input.setSelectionRange(cursor, cursor)
    })
  } else {
    titleSettingsForm.value.pattern = currentValue + placeholder
  }
}

const closeTitleSettingsModal = () => {
  showTitleSettingsModal.value = false
  selectedTemplateForTitle.value = null
  titleSettingsForm.value.pattern = ''
  titleSettingsForm.value.pdfTitle = ''
  titleSettingsForm.value.preview = ''
}

const saveTitleSettings = async () => {
  if (!selectedTemplateForTitle.value) return

  savingTitleSettings.value = true
  try {
    const rawPattern = titleSettingsForm.value.pattern ?? ''
    const trimmedPattern = rawPattern.trim()
    const rawPdfTitle = titleSettingsForm.value.pdfTitle ?? ''
    const trimmedPdfTitle = rawPdfTitle.trim()

    const payload = {
      pattern: trimmedPattern.length > 0 ? trimmedPattern : null,
      pdfTitle: trimmedPdfTitle.length > 0 ? trimmedPdfTitle : null
    }

    const hasPayload =
      payload.pattern !== null || payload.pdfTitle !== null

    await templateService.updateTeamTemplateTitleSettings(
      selectedTemplateForTitle.value.id,
      hasPayload ? payload : null
    )

    await loadTeamTemplates()
    refreshSelectedTemplateForFields()

    showSuccess('Titel-Einstellungen erfolgreich aktualisiert')
    closeTitleSettingsModal()
  } catch (err) {
    console.error('Error updating title settings:', err)
    showError(err.response?.data?.error || 'Fehler beim Aktualisieren der Titel-Einstellungen')
  } finally {
    savingTitleSettings.value = false
  }
}

const templateFields = computed(() => {

  if (!selectedTemplateForFields.value) {
    return []
  }
  
  
  const fields = selectedTemplateForFields.value.template?.fields || []
  return fields.sort((a, b) => {
    
    if (a.order !== b.order) {
      return (a.order || 0) - (b.order || 0)
    }
    return (a.displayName || '').localeCompare(b.displayName || '')
  })
})

const openCreateFieldModal = async () => {
  await loadTaxRates()
  if (!selectedTemplateForFields.value) {
  showError('Bitte wähle zuerst ein Template aus')
    return
  }
  
  
  if (!selectedTemplateForFields.value.template.allowCustomFields) {
    showError('Benutzerdefinierte Felder sind für dieses Template nicht erlaubt')
    return
  }
  
  
  newField.value = {
    name: '',
    displayName: '',
    type: 'text',
    category: 'receipt',
    required: false,
    allowPrefill: false,
    placeholder: '',
    description: '',
    defaultValue: '',
    isCalculated: false,
    calculationFormula: '',
    selectOptions: '',
    validation: {
      min: null,
      max: null
    },
    options: {
      includeTaxRate: false,
      availableTaxRates: [],
      unit: '',
      pricePerUnit: 0,
      dependsOn: '',
      taxRateSelectionMode: 'user_choice',
      selectedTaxRateId: '',
      resolvedTaxRates: [],
      // Repeater options
      repeaterMinItems: 0,
      repeaterMaxItems: null,
      repeaterIncludeTaxRate: false,
      repeaterTaxRateSelectionMode: 'user_choice',
      repeaterAvailableTaxRates: [],
      repeaterSelectedTaxRateId: '',
      groupId: null,
    }
  }
  
  showCreateFieldModal.value = true
  
  
  ensureValidDefaultTaxRate(newField.value.options)
}

const createField = async () => {
  if (!selectedTemplateForFields.value) return
  
  if (!newField.value.name || !newField.value.displayName) {
    showError('Name und Anzeigename sind erforderlich')
    return
  }
  
  
  if (newField.value.isCalculated && newField.value.type === 'money') {
    if (!newField.value.options.unit) {
      showError('Einheit ist für berechnete Felder erforderlich')
      return
    }
    if (!newField.value.options.pricePerUnit || newField.value.options.pricePerUnit <= 0) {
      showError('Preis pro Einheit muss größer als 0 sein')
      return
    }
  }
  
  
  if (newField.value.type === 'select') {
    if (!newField.value.selectOptions || newField.value.selectOptions.trim() === '') {
      showError('Auswahloptionen sind für Auswahl-Felder erforderlich')
      return
    }
  }

  if (
    newField.value.options.includeTaxRate &&
    newField.value.options.taxRateSelectionMode === 'user_choice'
  ) {
    const available = newField.value.options.availableTaxRates || []
    if (
      newField.value.options.selectedTaxRateId &&
      !available.includes(newField.value.options.selectedTaxRateId)
    ) {
      showError('Der Standard-Steuersatz muss zu den aktivierten Steuersätzen gehören')
      return
    }
  }
  
  creatingField.value = true
  
  try {
    await templateService.createCustomField(selectedTemplateForFields.value.id, newField.value)
    
    
    await loadTeamTemplates()
    refreshSelectedTemplateForFields()
    
    showSuccess('Feld erfolgreich erstellt')
    showCreateFieldModal.value = false
  } catch (err) {
    console.error('Error creating field:', err)
    showError(err.response?.data?.error || 'Fehler beim Erstellen des Feldes')
  } finally {
    creatingField.value = false
  }
}

const editField = async (field) => {

  editingField.value = { 
    ...field,
    selectOptions: field.selectOptions || '',
    allowPrefill: field.allowPrefill !== undefined ? field.allowPrefill : false,
    validation: {
      min: null,
      max: null,
      ...field.validation
    },
    options: {
      includeTaxRate: false,
      availableTaxRates: [],
      unit: '',
      pricePerUnit: 0,
      dependsOn: '',
      taxRateSelectionMode: 'user_choice',
      selectedTaxRateId: '',
      resolvedTaxRates: [],
      // Repeater options
      repeaterMinItems: 0,
      repeaterMaxItems: null,
      repeaterIncludeTaxRate: false,
      repeaterTaxRateSelectionMode: 'user_choice',
      repeaterAvailableTaxRates: [],
      repeaterSelectedTaxRateId: '',
      ...field.options
    }
}


  ensureValidDefaultTaxRate(editingField.value.options)

  await loadTaxRates()

  ensureValidDefaultTaxRate(editingField.value.options)

  showEditFieldModal.value = true
}

const updateField = async () => {
  if (!editingField.value.id) return
  

  
  
  if (editingField.value.isCalculated && editingField.value.type === 'money') {
    if (!editingField.value.options.unit) {
      showError('Einheit ist für berechnete Felder erforderlich')
      return
    }
    if (!editingField.value.options.pricePerUnit || editingField.value.options.pricePerUnit <= 0) {
      showError('Preis pro Einheit muss größer als 0 sein')
      return
    }
  }
  
  
  if (editingField.value.type === 'select') {
    if (!editingField.value.selectOptions || editingField.value.selectOptions.trim() === '') {
      showError('Auswahloptionen sind für Auswahl-Felder erforderlich')
      return
    }
  }

  if (
    editingField.value.options?.includeTaxRate &&
    editingField.value.options?.taxRateSelectionMode === 'user_choice'
  ) {
    const available = editingField.value.options.availableTaxRates || []
    if (
      editingField.value.options.selectedTaxRateId &&
      available.length > 0 &&
      !available.includes(editingField.value.options.selectedTaxRateId)
    ) {
      showError('Der Standard-Steuersatz muss zu den aktivierten Steuersätzen gehören')
      return
    }
  }
  
  updatingField.value = true
  
  try {
    
    let updateData = { ...editingField.value }
    
    if (editingField.value.undeletable) {
      
      updateData = {
        id: editingField.value.id,
        displayName: editingField.value.displayName,
        description: editingField.value.description,
        placeholder: editingField.value.placeholder,
        defaultValue: editingField.value.defaultValue,
        validation: editingField.value.validation,
        options: editingField.value.options,
        
      }
    }
    
    await templateService.updateTemplateField(editingField.value.id, updateData)
    
    
    await loadTeamTemplates()
    refreshSelectedTemplateForFields()
    
    showSuccess('Feld erfolgreich aktualisiert')
    showEditFieldModal.value = false
  } catch (err) {
    console.error('Error updating field:', err)
    showError(err.response?.data?.error || 'Fehler beim Aktualisieren des Feldes')
  } finally {
    updatingField.value = false
  }
}

const deleteField = async (field) => {
  
  if (field.undeletable) {
    showError('Fixed-Felder können nicht gelöscht werden')
    return
  }
  
  const confirmed = await showConfirmDialog(
    'Feld löschen',
    `Möchtest du das Feld "${field.displayName}" wirklich löschen?`,
    'Löschen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#dc3545'
    }
  )
  
  if (!confirmed.isConfirmed) return
  
  try {
    await templateService.deleteTemplateField(field.id)
    
    
    await loadTeamTemplates()
    refreshSelectedTemplateForFields()
    
    showSuccess('Feld erfolgreich gelöscht')
  } catch (err) {
    console.error('Error deleting field:', err)
    showError(err.response?.data?.error || 'Fehler beim Löschen des Feldes')
  }
}



const getFieldTypeLabel = (type) => {
  const typeLabels = {
    text: 'Text',
    number: 'Zahl',
    date: 'Datum',
    select: 'Auswahl',
    checkbox: 'Checkbox',
    textarea: 'Textbereich',
    file: 'Datei',
    money: 'Geldbetrag',
    location: 'Standort'
  }
  return typeLabels[type] || type
}


const getFieldsByCategory = (category) => {
  if (!templateFields.value) return []
  
  return templateFields.value
    .filter(field => field.category === category)
    .sort((a, b) => {
      
      if (a.order !== b.order) {
        return (a.order || 0) - (b.order || 0)
      }
    return (a.displayName || '').localeCompare(b.displayName || '')
  })
}

const countFieldsInGroup = (category, groupId) => {
  if (!groupId) {
    return 0
  }
  return getFieldsByCategory(category).filter(
    (field) => (field.options?.groupId || null) === groupId
  ).length
}

const clearFieldGroupAssignments = async (category, groupId) => {
  const fields = getFieldsByCategory(category)
  const affected = fields.filter((field) => (field.options?.groupId || null) === groupId)

  if (affected.length === 0) {
    return
  }

  try {
    await Promise.all(
      affected.map((field) => {
        const updatedOptions = { ...(field.options || {}), groupId: null }
        return templateService
          .updateTemplateField(field.id, { options: updatedOptions })
          .then(() => {
            field.options = updatedOptions
            const target =
              selectedTemplateForFields.value?.template?.fields?.find((f) => f.id === field.id)
            if (target) {
              target.options = updatedOptions
            }
          })
      })
    )
    showSuccess('Felder ohne Gruppe aktualisiert')
  } catch (err) {
    console.error('Error clearing field group assignments:', err)
    showError(err.response?.data?.error || 'Fehler beim Aktualisieren der Feld-Gruppen')
  }
}

const removeFieldGroup = async (category, group) => {
  if (!FIELD_GROUP_CATEGORIES.includes(category) || !group) {
    return
  }

  const confirmation = await showConfirmDialog(
    'Gruppe löschen',
    `Möchtest du die Gruppe "${group.name}" wirklich löschen? Zugeordnete Felder werden der Gruppe entfernt.`,
    'Gruppe löschen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#dc3545'
    }
  )

  if (!confirmation.isConfirmed) {
    return
  }

  const remaining = getFieldGroups(category)
    .filter((existing) => existing.id !== group.id)
    .map((existing, index) => ({
      ...existing,
      order: index
    }))

  fieldGroupsState.value = {
    ...fieldGroupsState.value,
    [category]: remaining
  }
  markFieldGroupsDirty()

  await clearFieldGroupAssignments(category, group.id)
}

const onFieldGroupAssignmentChange = async (field, category, value) => {
  if (!field || !FIELD_GROUP_CATEGORIES.includes(category)) {
    return
  }

  const normalizedGroupId = value && value.length > 0 ? value : null
  const updatedOptions = { ...(field.options || {}), groupId: normalizedGroupId }

  updatingFieldGroupId.value = field.id
  try {
    await templateService.updateTemplateField(field.id, { options: updatedOptions })
    field.options = updatedOptions

    const target = selectedTemplateForFields.value?.template?.fields?.find(
      (f) => f.id === field.id
    )
    if (target) {
      target.options = updatedOptions
    }

    showSuccess('Feld-Gruppe aktualisiert')
  } catch (err) {
    console.error('Error updating field group assignment:', err)
    showError(err.response?.data?.error || 'Fehler beim Aktualisieren der Feld-Gruppe')
  } finally {
    updatingFieldGroupId.value = null
  }
}

const toggleOrderMode = (category) => {
  if (orderMode.value === category) {
    
    orderMode.value = null
    draggedField.value = null
    draggedCategory.value = null
  } else {
    
    orderMode.value = category
  }
}

const dragStart = (event, field, category) => {
  if (orderMode.value !== category) return
  
  draggedField.value = field
  draggedCategory.value = category
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', field.id)
}

const drop = (event, targetField, category) => {
  if (orderMode.value !== category || !draggedField.value) return
  
  event.preventDefault()
  
  const fields = getFieldsByCategory(category)
  const draggedIndex = fields.findIndex(f => f.id === draggedField.value.id)
  const targetIndex = fields.findIndex(f => f.id === targetField.id)
  
  if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) return
  
  
  const reorderedFields = [...fields]
  const [movedField] = reorderedFields.splice(draggedIndex, 1)
  reorderedFields.splice(targetIndex, 0, movedField)
  
  
  reorderedFields.forEach((field, index) => {
    field.order = index + 1
  })
  
  
  reorderedFields.forEach(field => {
    const templateField = templateFields.value.find(f => f.id === field.id)
    if (templateField) {
      templateField.order = field.order
    }
  })
  
  draggedField.value = null
  draggedCategory.value = null
}

const saveFieldOrder = async (category) => {
  if (!selectedTemplateForFields.value) return
  
  savingOrder.value = true
  
  try {
    const fields = getFieldsByCategory(category)
    const orderUpdates = fields.map((field, index) => ({
      id: field.id,
      order: index + 1
    }))
    
    
    await Promise.all(
      orderUpdates.map(update => 
        templateService.updateTemplateField(update.id, { order: update.order })
      )
    )
    
    showSuccess(`Reihenfolge der ${getFieldCategoryLabel(category)}-Felder gespeichert`)
    orderMode.value = null
    
    
    await loadTeamTemplates()
    refreshSelectedTemplateForFields()
  } catch (err) {
    console.error('Error saving field order:', err)
    showError('Fehler beim Speichern der Reihenfolge')
  } finally {
    savingOrder.value = false
  }
}

const getFieldCategoryLabel = (category) => {
  const categoryLabels = {
    personal: 'Persönlich',
    receipt: 'Quittung',
    financial: 'Finanziell'
  }
  return categoryLabels[category] || category
}

watch(() => titleSettingsForm.value.pattern, () => {
  updateTitlePreview()
})

watch(() => selectedTemplateForTitle.value, () => {
  updateTitlePreview()
})

// Watch for category changes and reset type if not allowed
watch(() => newField.value.category, (newCategory) => {
  if (newField.value.type === 'repeater' && newCategory !== 'receipt') {
    newField.value.type = 'text'
  }
  if (newField.value.type === 'money' && newCategory !== 'financial') {
    newField.value.type = 'text'
  }
  const groups = getFieldGroups(newCategory)
  if (!groups.some((group) => group.id === newField.value.options.groupId)) {
    newField.value.options.groupId = null
  }
})

watch(() => editingField.value.category, (newCategory) => {
  if (editingField.value.type === 'repeater' && newCategory !== 'receipt') {
    editingField.value.type = 'text'
  }
  if (editingField.value.type === 'money' && newCategory !== 'financial') {
    editingField.value.type = 'text'
  }
  const groups = getFieldGroups(newCategory)
  if (!groups.some((group) => group.id === editingField.value.options.groupId)) {
    editingField.value.options.groupId = null
  }
})

watch(
  () => newField.value.options,
  (options) => {
    ensureValidDefaultTaxRate(options)
  },
  { deep: true }
)

watch(
  () => editingField.value.options,
  (options) => {
    ensureValidDefaultTaxRate(options)
  },
  { deep: true }
)

watch(showFieldsModal, (isOpen) => {
  if (isOpen) {
    initializeFieldGroups()
  } else {
    fieldGroupsDirty.value = false
  }
})

watch(
  () => selectedTemplateForFields.value?.id,
  () => {
    if (showFieldsModal.value) {
      initializeFieldGroups()
    }
  }
)

onMounted(async () => {
  if (!selectedTeam.value || selectedTeam.value.id !== route.params.id) {
    await teamsStore.loadTeamById(route.params.id)
  }
  await loadTaxRates()
  await loadTeamTemplates()
})
</script>

<style scoped>
.nav-link.active{
  background: var(--bs-primary) !important;
}

.field-group-item {
  background: #fff;
  transition: background-color 0.2s ease;
}

.field-group-item:hover {
  background-color: #f8f9fa;
}

.group-color-dot {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  display: inline-block;
}
</style>
